import {
  UuidParams,
  UuidSchema,
  __name
} from "./chunk-KDZI72TC.js";

// src/index.ts
import { z as z2 } from "zod";
import { uuidv7 } from "uuidv7";
import { getCountries, parsePhoneNumberFromString, isSupportedCountry } from "libphonenumber-js";

// src/form-helpers.ts
function zodFieldValidator(schema) {
  return ({ value }) => {
    const result = schema.safeParse(value);
    return result.success ? void 0 : result.error.issues[0]?.message;
  };
}
__name(zodFieldValidator, "zodFieldValidator");
function shouldShowFieldError(form, field) {
  return (form.state.submissionAttempts > 0 || field.state.meta.isBlurred) && field.state.meta.errors.length > 0;
}
__name(shouldShowFieldError, "shouldShowFieldError");

// src/retention-config.schema.ts
import { z } from "zod";
var retentionConfigSchema = z.object({
  notifications: z.number().int().positive().optional(),
  auditLog: z.number().int().positive().optional(),
  customerDocuments: z.number().int().positive().optional()
});

// src/index.ts
var TERMS_VERSION = "v1";
var MoneySchema = z2.union([
  z2.number(),
  z2.string()
]).transform((val, ctx) => {
  const n = typeof val === "string" ? parseInt(val, 10) : val;
  if (typeof val === "string" && String(n) !== val.trim()) {
    ctx.addIssue({
      code: z2.ZodIssueCode.custom,
      message: `Money value must be an integer (paise): received ${val}`
    });
    return z2.NEVER;
  }
  if (!Number.isInteger(n)) {
    ctx.addIssue({
      code: z2.ZodIssueCode.custom,
      message: `Money value must be an integer (paise): received ${val}`
    });
    return z2.NEVER;
  }
  return n;
}).pipe(z2.number().int().nonnegative());
function generateUuidv7() {
  return uuidv7();
}
__name(generateUuidv7, "generateUuidv7");
var COUNTRIES = getCountries();
var CountryCodeSchema = z2.enum(COUNTRIES);
function normalizePhone(raw, defaultRegion) {
  try {
    if (typeof raw !== "string" || !raw.trim()) {
      return null;
    }
    const envDefault = typeof process !== "undefined" && process.env ? process.env.DEFAULT_COUNTRY : void 0;
    const rawRegion = defaultRegion ?? envDefault ?? "IN";
    const region = typeof rawRegion === "string" ? rawRegion.trim().toUpperCase() : "IN";
    const defaultCountry = isSupportedCountry(region) ? region : "IN";
    const parsed = parsePhoneNumberFromString(raw.trim(), defaultCountry);
    if (!parsed || !parsed.isValid() || !parsed.country) {
      return null;
    }
    return {
      country: parsed.country,
      nationalNumber: parsed.nationalNumber,
      e164: parsed.number
    };
  } catch {
    return null;
  }
}
__name(normalizePhone, "normalizePhone");
var PhoneSchema = z2.string().transform((val, ctx) => {
  const normalized = normalizePhone(val);
  if (!normalized) {
    ctx.addIssue({
      code: z2.ZodIssueCode.custom,
      message: "Invalid phone number"
    });
    return z2.NEVER;
  }
  return normalized.e164;
});
var PhonePartsSchema = z2.union([
  z2.string(),
  z2.object({
    countryCode: z2.string().optional(),
    country: z2.string().optional(),
    nationalNumber: z2.string()
  })
]).transform((val, ctx) => {
  let raw;
  let region;
  if (typeof val === "string") {
    raw = val;
  } else {
    raw = val.nationalNumber;
    region = val.countryCode ?? val.country;
  }
  const normalized = normalizePhone(raw, region);
  if (!normalized) {
    ctx.addIssue({
      code: z2.ZodIssueCode.custom,
      message: "Invalid phone number"
    });
    return z2.NEVER;
  }
  return {
    countryCode: normalized.country,
    nationalNumber: normalized.nationalNumber
  };
});
var PanSchema = z2.string().regex(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, "Invalid PAN format");
var AadhaarHashSchema = z2.string().length(64, "Aadhaar hash must be 64 hex chars (SHA-256)");
var PincodeSchema = z2.string().regex(/^[1-9][0-9]{5}$/, "Invalid Indian pincode");
var MpinSchema = z2.string().regex(/^\d{6}$/, "MPIN must be exactly 6 digits");
var EmailSchema = z2.string().email("Invalid email format").max(255).transform((v) => v.toLowerCase().trim()).pipe(z2.string().email());
var PasswordSchema = z2.string().min(8, "Password must be at least 8 characters").max(128, "Password must be at most 128 characters");
var IfscSchema = z2.string().regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, "Invalid IFSC format");
var AccountNumberSchema = z2.string().regex(/^\d{9,18}$/, "Account number must be 9-18 digits");
var GstinSchema = z2.string().regex(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, "Invalid GSTIN format").transform((v) => v.toUpperCase()).pipe(z2.string().regex(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/));
var CinSchema = z2.string().regex(/^[A-Z]{1}[0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/, "Invalid CIN format").transform((v) => v.toUpperCase()).pipe(z2.string().regex(/^[A-Z]{1}[0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/));
var DateString = z2.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be YYYY-MM-DD format");
var DateTimeString = z2.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/, "DateTime must be ISO 8601 format");
function omitServerFields(schema) {
  return schema.omit({
    deletedAt: true
  });
}
__name(omitServerFields, "omitServerFields");
var SuccessEnvelopeSchema = /* @__PURE__ */ __name((dataSchema) => z2.object({
  data: dataSchema
}), "SuccessEnvelopeSchema");
var PaginatedEnvelopeSchema = /* @__PURE__ */ __name((itemSchema) => z2.object({
  data: z2.array(itemSchema),
  meta: z2.object({
    total: z2.number().int().nonnegative(),
    page: z2.number().int().positive(),
    limit: z2.number().int().positive(),
    totalPages: z2.number().int().optional()
  })
}), "PaginatedEnvelopeSchema");
export {
  AadhaarHashSchema,
  AccountNumberSchema,
  COUNTRIES,
  CinSchema,
  CountryCodeSchema,
  DateString,
  DateTimeString,
  EmailSchema,
  GstinSchema,
  IfscSchema,
  MoneySchema,
  MpinSchema,
  PaginatedEnvelopeSchema,
  PanSchema,
  PasswordSchema,
  PhonePartsSchema,
  PhoneSchema,
  PincodeSchema,
  SuccessEnvelopeSchema,
  TERMS_VERSION,
  UuidParams,
  UuidSchema,
  generateUuidv7,
  normalizePhone,
  omitServerFields,
  retentionConfigSchema,
  shouldShowFieldError,
  zodFieldValidator
};
//# sourceMappingURL=index.js.map